import { useRef, useEffect, useMemo, memo, type ComponentType } from 'react';
import '../../styles/hover-draw-pictogram.css';

/**
 * Splits compound SVG path strings into individual subpaths by `M`/`m` commands.
 * Each subpath becomes its own `<path>` element for staggered draw animation.
 */
function splitSubpaths(paths: string[]): string[] {
  const segments: string[] = [];
  for (const d of paths) {
    // Split on M commands while keeping the M with the segment
    const parts = d.split(/(?=M)/g).filter((s) => s.trim().length > 0);
    segments.push(...parts);
  }
  return segments;
}

/**
 * Measures each SVG path's total length on mount and sets --path-length,
 * --path-index, and --path-count as CSS custom properties.
 */
function useMeasurePaths(segmentCount: number) {
  const refs = useRef<(SVGPathElement | null)[]>([]);
  useEffect(() => {
    refs.current.forEach((el, i) => {
      if (!el) return;
      el.style.setProperty('--path-length', String(Math.ceil(el.getTotalLength())));
      el.style.setProperty('--path-index', String(i));
      el.style.setProperty('--path-count', String(segmentCount));
    });
  }, [segmentCount]);
  return refs;
}

/**
 * Activates transitions on the closest .picto-hover-trigger on first hover.
 * Prevents the draw animation from playing on mount.
 */
function useActivateOnFirstHover(containerRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const trigger = el.closest('.picto-hover-trigger');
    if (!trigger) return;

    const activate = () => {
      trigger.classList.add('picto-hover-active');
      trigger.removeEventListener('mouseenter', activate);
    };
    trigger.addEventListener('mouseenter', activate);
    return () => trigger.removeEventListener('mouseenter', activate);
  }, [containerRef]);
}

export interface HoverDrawPictogramProps {
  width?: number;
  height?: number;
  className?: string;
  /** When true, renders a plain filled SVG with no animation layers. */
  static?: boolean;
}

/**
 * Creates a memoized pictogram component with a two-layer hover-draw animation.
 * The parent must have the `picto-hover-trigger` class for hover activation.
 */
export function createHoverDrawPictogram(
  displayName: string,
  paths: string[]
): ComponentType<HoverDrawPictogramProps> {
  const segments = splitSubpaths(paths);

  const Component = memo(function HoverDrawPictogram({
    width = 64,
    height = 64,
    className,
    static: isStatic,
  }: HoverDrawPictogramProps) {
    const refs = useMeasurePaths(segments.length);
    const containerRef = useRef<HTMLDivElement>(null);
    useActivateOnFirstHover(containerRef);

    if (isStatic) {
      return (
        <svg
          viewBox="0 0 32 32"
          width={width}
          height={height}
          fill="currentColor"
          className={className}
        >
          {paths.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </svg>
      );
    }

    return (
      <div ref={containerRef} className={`relative ${className ?? ''}`} style={{ width, height }}>
        <div className="picto-hover-base absolute inset-0 opacity-40">
          <svg viewBox="0 0 32 32" width={width} height={height} fill="currentColor">
            {paths.map((d, i) => (
              <path key={i} d={d} />
            ))}
          </svg>
        </div>
        <div className="pictogram-hover-draw absolute inset-0">
          <svg viewBox="0 0 32 32" width={width} height={height} fill="currentColor">
            {segments.map((d, i) => (
              <path
                key={i}
                d={d}
                ref={(el) => {
                  refs.current[i] = el;
                }}
              />
            ))}
          </svg>
        </div>
      </div>
    );
  });

  Component.displayName = displayName;
  return Component;
}
